// Copyright (c) 2025-2026, IPivot. All rights reserved.
'use strict';

import { useState, useEffect, useContext } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import { StatusContext } from './StatusContext';
import { mergeLayers, layerStats } from '../data';
import LayerStats from './LayerStats';
import { useNavigate } from 'react-router-dom';

/*
 * merge layers tools for fingerprints
 */
function MergeLayerPage() {
    const [ tlayer, setTLayer ] = useState<string>('0');
    const [ slayers, setSLayers ] = useState<string>('');
    const [ stats, setStats ] = useState<any>([]);
    const [ refresh, setRefresh ] = useState<boolean>(true);
    const statusContext = useContext(StatusContext);
    const navigate = useNavigate();

    useEffect(() => {
        async function getStats() {
            if (!refresh) {
                return;
            }
            try {
                let res = await layerStats();
                setStats(res);
            } catch (error : any) {
                console.error('Error fetching layer stats:', error);
                statusContext.postError(`Error: ${error.message}`);
            } finally {
                setRefresh(false);
            }
        }
        getStats();
    }, [refresh]);

    function doMerge() {
        navigate(`/probe/exec`);
        mergeLayers(slayers, tlayer).then(() => {
            statusContext.postError(`Merge operation done, check Exec page for progress`);
        }).catch(e => {
            console.log('Error merging layers:', e.message, e.stack);
            statusContext.postError(`Merge Error: ${e.message}`);
        });
    }

    return (
        <Container fluid>
            <Row className="px-4">
                <Col className="col-10">
                    <h3 className="mt-4">Merge Layers</h3>
                    <p>
                        Fold fingerprints from some or all layers to a single layer.
                        If there are duplicates the fingerprint on the highest layer is kept.
                        This will not delete fingerprints other than duplicates.
                    </p>
                    <Container className="p-0">
                        <Row>
                            <Form.Group as={Col} className="mb-3" controlId="f_slayer">
                                <Form.Label>Source Layers</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={slayers}
                                    placeholder="Layer numbers, comma separated"
                                    onChange={(e) => {
                                        setSLayers(e.target.value);
                                    }} />
                                <Form.Text className="text-muted">
                                    Enter the layer numbers to merge, separated by commas. Leave blank for all layers.
                                </Form.Text>
                            </Form.Group>
                        </Row>
                        <Row>
                            <Form.Group as={Col} className="mb-3" controlId="f_tlayer">
                                <Form.Label>Target Layer</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={tlayer}
                                    placeholder="Target layer number (required)"
                                    onChange={(e) => {
                                        setTLayer(e.target.value);
                                    }} />
                                <Form.Text className="text-muted">
                                    Enter the target layer number to merge into.
                                </Form.Text>
                            </Form.Group>
                        </Row>
                        <Row className="mt-3">
                            <Col>
                                <Button variant="primary" onClick={doMerge}>
                                    Merge Layers
                                </Button>
                            </Col>
                        </Row>
                        <Row>
                            <Col className="mt-3">
                                <h5>Layer Stats</h5>
                                <LayerStats data={{bylayer: stats}} />
                            </Col>
                        </Row>
                    </Container>
                </Col>
            </Row>
        </Container>
    );
}

export default MergeLayerPage;
